import { useEffect, useState } from "react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";

//--- Services ---
import stationSvc from "../../../services/api/station";
import stationParameterSvc from "../../../services/api/stationParameter";
import stationCameraSvc from "../../../services/api/stationCamera";
import stationParameterWarningSvc from "../../../services/api/stationParameterWarnings";

//--- Components ---
import StationCreateUpdate from "./station";
import StationParameter from "./stationParameter";
import StationCamera from "./stationCamera";
import StationParameterWarnings from "./stationParameterWarnings";

//--- Models ---
import { StationItem } from "../../../models/types/station";
import { StationParameterItem } from "../../../models/types/stationParameter";
import { StationParameterWarningItem } from "../../../models/types/stationParameterWarnings";
import { StationCameraItem } from "../../../models/types/stationCamera";

//--- Utils ---
import Loading from "../../../components/loading/loading";
import ButtonGoback from "../../../components/button/buttonGoback";
import { notify } from "../../../common/notificationService";
import { useNavigate } from "react-router-dom";
import { UrlCollection } from "../../../common/url-collection";
import { useTranslation } from "react-i18next";

export default function StationCreateUpdatePage() {
    const navigate = useNavigate();
    const { id } = useParams<{ id?: string }>();
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    const [data, setData] = useState<StationItem | null>(null);
    const [currentTab, setCurrentTab] = useState<string>('general');
    const [loading, setLoading] = useState(false);
    const [listParameter, setListParameter] = useState<StationParameterItem[]>([]);
    const [listCamera, setListCamera] = useState<StationCameraItem[]>([]);
    const [listParameterWarning, setListParameterWarning] = useState<StationParameterWarningItem[]>([]);

    const listTab = [
        { id: 'general', disabled: false, name: t("station.tabs.general") },
        { id: 'parameter', disabled: !data, name: t("station.tabs.parameter") },
        { id: 'camera', disabled: !data, name: t("station.tabs.camera") },
        { id: 'parameterWarnings', disabled: !data, name: t("station.tabs.parameterWarnings") }
    ];

    useEffect(() => {
        if (id) {
            getStationById(id);
            getListParameter(id);
            getListCamera(id);
            getListParameterWarning(id);
        }
    }, [id]);

    const changeTab = async () => {
        const queryParams = new URLSearchParams(location.search);
        const params: { tab: string } = {
            tab: queryParams.get("tab") || "general",
        };
        handleTabChange(params.tab);
    };

    useEffect(() => {
        if (data) {
            changeTab();
        }
    }, [location.search, data]);

    const getStationById = async (id: string) => {
        setLoading(true);
        try {
            const res = await stationSvc.getStationById(id);
            setData(res.content);
        } catch (e: any) {
            setLoading(false);
            notify(t("common.error"), t("common.errorMessage"), "danger");
        } finally {
            setLoading(false);
        }
    };

    const getListParameter = async (stationId: string) => {
        try {
            const res = await stationParameterSvc.getAll({ pageIndex: 1, pageSize: 100, stationId });
            if (res && res.content) {
                setListParameter(res.content.items);
            }
        } catch (e: any) {
            notify(t("common.error"), t("common.errorMessage"), "danger");
        }
    };

    const getListCamera = async (stationId: string) => {
        try {
            const res = await stationCameraSvc.getAll({ pageIndex: 1, pageSize: 100, stationId });
            if (res && res.content) {
                setListCamera(res.content.items);
            }
        } catch (e: any) {
            notify(t("common.error"), t("common.errorMessage"), "danger");
        }
    };

    const getListParameterWarning = async (stationId: string) => {
        try {
            const res = await stationParameterWarningSvc.getAll({ pageIndex: 1, pageSize: 100, stationId });
            if (res && res.content) {
                setListParameterWarning(res.content.items);
            }
        } catch (e: any) {
            notify(t("common.error"), t("common.errorMessage"), "danger");
        }
    };

    const handleTabChange = (tabId: string) => {
        if (!listTab.find((item) => item.id === tabId)?.disabled) {
            setCurrentTab(tabId);
        }
    };

    return (
        <div className="w-full h-full flex flex-col gap-3 p-6">
            <div>
                <ButtonGoback
                    onClick={() => navigate(UrlCollection.Station)}
                />
            </div>
            <div className="w-full h-[calc(100%-38px)] transition-all duration-300 flex xl:flex-row flex-1 flex-col gap-4">
                <div className="xl:w-[200px] xl:h-full flex xl:flex-col flex-wrap"> {/* sidebar */}
                    {listTab.map((item) => (
                        <div
                            key={item.id}
                            className={`px-4 py-2 shadow-md border-b-2 border-t-2 border-gray-200
                                ${currentTab === item.id ? 'bg-blue-500 text-white' : 'bg-white hover:bg-blue-300 hover:text-white'}
                                ${item.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            onClick={() => data && setSearchParams({ tab: item.id })}
                        >
                            <span className={`${item.disabled ? 'text-gray-500' : ''} font-semibold text-md`}>
                                {item.name}
                            </span>
                        </div>
                    ))}
                </div>
                <div className="h-full w-full flex-1 xl:flex-1 overflow-y-auto"> {/* main */}
                    {loading ? <Loading /> : (
                        <>
                            {currentTab === 'general' &&
                                <StationCreateUpdate
                                    isCreate={!data?.id}
                                    data={data}
                                    onSuccess={getStationById}
                                />
                            }
                            {currentTab === 'parameter' && data &&
                                <StationParameter
                                    stationId={data.id}
                                    onSuccess={() => getListParameter(data.id.toString())}
                                    data={listParameter} />}
                            {currentTab === 'camera' && data &&
                                <StationCamera
                                    stationId={data.id}
                                    data={listCamera}
                                    onSuccess={() => getListCamera(data.id.toString())}
                                />
                            }
                            {currentTab === 'parameterWarnings' && data &&
                                <StationParameterWarnings
                                    stationId={data.id}
                                    data={listParameterWarning}
                                    onSuccess={() => getListParameterWarning(data.id.toString())}
                                />
                            }
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}